import { useCallback, useLayoutEffect, useMemo } from "react";
import { useRouter } from "expo-router";
import { useSites } from "@src/features/cameras/hooks/useSites";
import { useSiteNames } from "@src/features/cameras/hooks/useSiteNames";
import { useCameras } from "@src/features/cameras";
import { useInviteUser, useIsTenantAdmin, isLegacyCamera } from "@visioni/a3s-access/app";
import type { InviteUserFormData, SiteAccessListSite, CameraAccessListCamera } from "@visioni/a3s-access/app";
import type { Role } from "@visioni/a3s-access/schemas";
import { showSuccessToast, showErrorToast } from "@src/utils/toast";
import { resolveCameraName } from "@src/features/cameras/utils/camera-display-name";

export function useInviteUserPage() {
  const router = useRouter();
  const { isTenantAdmin, isLoading: permissionsLoading, isError: permissionsError } = useIsTenantAdmin();
  const { sites, isLoading: sitesLoading } = useSites();
  const siteNames = useSiteNames();
  const { data: cameras, isLoading: camerasLoading } = useCameras();
  const inviteUser = useInviteUser();

  const siteList: SiteAccessListSite[] = useMemo(() => {
    if (!sites) return [];
    return sites.map((s) => {
      const siteId = String(s.siteId);
      const firstCamera = cameras?.find((c) => c.site.id === siteId);
      return {
        id: siteId,
        name: s.siteName,
        snapshotUrl: firstCamera?.snapshot?.url ?? null,
      };
    });
  }, [sites, cameras]);

  const cameraList: CameraAccessListCamera[] = useMemo(() => {
    if (!cameras || !siteNames) return [];
    return cameras.map((c) => {
      const cameraKey = `${c.key.asset}@${c.key.id}`;
      return {
        id: `${c.site.id}/${cameraKey}`,
        name: resolveCameraName(c, cameraKey),
        siteId: c.site.id,
        siteName: siteNames.get(c.site.id) ?? c.site.id,
        snapshotUrl: c.snapshot?.url ?? null,
        legacy: isLegacyCamera(`${c.site.id}/${cameraKey}`),
      };
    });
  }, [cameras, siteNames]);

  const isLoading = sitesLoading || camerasLoading;

  useLayoutEffect(() => {
    if (!permissionsLoading && !permissionsError && !isTenantAdmin) {
      router.replace("/forbidden");
    }
  }, [permissionsLoading, permissionsError, isTenantAdmin, router]);

  const navigateBack = useCallback(() => {
    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace("/settings/users-and-permissions");
    }
  }, [router]);

  const handleSubmit = useCallback(
    (data: InviteUserFormData) => {
      const roles: Role[] = [];
      if (data.tenantAdmin) roles.push("tenant_admin");
      for (const [siteId, role] of data.sites) {
        roles.push(role === "manager" ? `site_manager:${siteId}` : `site_user:${siteId}`);
      }

      const cameraPermissions = [...data.cameras.entries()].map(([cameraId, role]) => ({
        camera: cameraId,
        role: role === "manager" ? ("camera_manager" as const) : ("camera_viewer" as const),
      }));

      inviteUser.mutate(
        { email: data.email, roles, cameraPermissions },
        {
          onSuccess: () => {
            showSuccessToast({ message: `Invited ${data.email}` });
            navigateBack();
          },
          onError: (error) => {
            showErrorToast({
              message:
                error.message.includes("does not have an existing account") ?
                  "This user doesn't have an account yet. Please contact support to create one."
                : "Failed to invite user",
            });
          },
        },
      );
    },
    [inviteUser, navigateBack],
  );

  const handleCancel = navigateBack;

  return {
    siteList,
    cameraList,
    isLoading,
    isPending: inviteUser.isPending,
    handleSubmit,
    handleCancel,
  };
}
